import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { DateFormatterIso } from '../../shared/utilities/date-formatter.utils';
import { getNameInitial } from '../../shared/utilities/get-name-initial.utils';
import { getProfileImageUrl } from '../../shared/utilities/users.utils';
import { CommonService } from '../../shared/services/common/common.service';
import { AuditLogService } from '../../shared/services/audit-log/audit-log.service';
import { HotelService } from '../../shared/services/hotel/hotel.service';
import { PMSRouteEnum } from '../../shared/enums/route.enum';
import type {
  AuditLogItem,
  AuditLogQueryParams,
  AuditLogListResult,
} from '../../shared/interfaces/audit-log/audit-log.interface';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import {
  setSearchParams,
  toQueryParams,
} from '../../shared/utilities/common.utils';

const ACTION_OPTIONS = [
  { label: 'All Actions', value: '' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
];

@Component({
  selector: 'app-audit-logs',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TableModule,
    InputTextModule,
    IconFieldModule,
    InputIcon,
    DatePickerModule,
    Select,
  ],
  templateUrl: './audit-logs.component.html',
  styleUrl: './audit-logs.component.scss',
})
export class AuditLogsComponent {
  readonly DateFormatterIso = DateFormatterIso;
  readonly PMSRouteEnum = PMSRouteEnum;
  readonly actionOptions = ACTION_OPTIONS;
  readonly getNameInitial = getNameInitial;
  readonly getProfileImageUrl = getProfileImageUrl;
  readonly localStorageService = inject(LocalStorageService);

  logs: AuditLogItem[] = [];
  total = 0;
  page = 1;
  limit = 10;
  totalPages = 0;
  loading = false;

  filters: AuditLogQueryParams = {
    action: '',
    entity: '',
    propertyId:
      this.localStorageService.getSessionPropertyStaff()?.tenantId ?? '',
    dateFrom: '',
    dateTo: '',
    search: '',
    page: this.page,
    limit: this.limit,
  };

  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  /** Entity filter options from /audit/applicable-entities (with "All Entities" option). */
  entityOptions: { label: string; value: string }[] = [
    { label: 'All Entities', value: '' },
  ];

  /** Property filter options for admin (from property get-all). Shown only when isAdminRoute. */
  propertyOptions: { label: string; value: string }[] = [
    { label: 'All Properties', value: '' },
  ];

  /** True when current route is under admin (e.g. /admin/audit/logs). */
  get isAdminRoute(): boolean {
    return this.router.url.includes('/admin/');
  }

  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly FILTER_DEBOUNCE_MS = 400;

  constructor(
    public readonly commonService: CommonService,
    private readonly auditLogService: AuditLogService,
    private readonly hotelService: HotelService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.auditLogService.getApplicableEntities().subscribe((resultset) => {
      this.entityOptions = [
        { label: 'All Entities', value: '' },
        ...resultset,
      ];
    });
    this.hotelService.getAllProperty().subscribe({
      next: (list) => {
        const arr = Array.isArray(list) ? list : [];
        this.propertyOptions = [
          { label: 'All Properties', value: '' },
          ...arr.map((p: { id?: string; name?: string }) => ({
            label: p?.name ?? p?.id ?? '',
            value: p?.id ?? '',
          })),
        ];
      },
    });
    this.route.queryParams.subscribe((qp) => {
      this.filters.action = qp['action'] ?? '';
      this.filters.entity = qp['entity'] ?? '';

      this.filters.propertyId =
        qp['propertyId'] ??
        (this.isAdminRoute
          ? ''
          : this.localStorageService.getSessionPropertyStaff()?.propertyId ?? '');
      this.filters.actorUserId = qp['actorUserId'] ?? '';
      this.filters.search = qp['search'] ?? '';
      this.page = parseInt(qp['page'] ?? String(this.page), 10);
      this.limit = parseInt(qp['limit'] ?? String(this.limit), 10);
      this.filters.page = this.page;
      this.filters.limit = this.limit;
      if (qp['dateFrom']) this.dateFrom = new Date(qp['dateFrom']);
      else this.dateFrom = null;
      if (qp['dateTo']) this.dateTo = new Date(qp['dateTo']);
      else this.dateTo = null;

      setSearchParams(this.getQueryParams());
      this.loadLogs();
    });
  }

  loadLogs(): void {
    this.loading = true;
    const params: AuditLogQueryParams = {
      page: this.page,
      limit: this.limit,
    };
    if (this.filters.action) params.action = this.filters.action;
    if (this.filters.entity) params.entity = this.filters.entity;
    if (this.filters.propertyId) params.propertyId = this.filters.propertyId;
    if (this.filters.actorUserId) params.actorUserId = this.filters.actorUserId;
    if (this.filters.search) params.search = this.filters.search;
    if (this.dateFrom) {
      params.dateFrom = this.dateFrom.toISOString().split('T')[0];
    }
    if (this.dateTo) {
      params.dateTo = this.dateTo.toISOString().split('T')[0];
    }

    this.auditLogService.getLogs(params).subscribe({
      next: (res: AuditLogListResult) => {
        this.logs = res?.data ?? [];
        this.total = res?.total ?? 0;
        this.page = res?.page ?? 1;
        this.limit = res?.limit ?? 20;
        this.totalPages = res?.totalPages ?? 0;
        this.loading = false;
      },
      error: () => {
        this.logs = [];
        this.total = 0;
        this.loading = false;
      },
    });
  }

  onLazyLoad(event: { first?: number | null; rows?: number | null }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.limit;
    this.page = rows ? Math.floor(first / rows) + 1 : 1;
    this.limit = rows;
    this.filters.page = this.page;
    this.filters.limit = this.limit;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  /** Trigger load when any single filter changes (replaces Apply button). */
  onFilterChange(): void {
    this.page = 1;
    this.filters.page = 1;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  /** Debounced trigger for text inputs (search, entity, entityId). */
  onFilterChangeDebounced(): void {
    if (this.filterDebounceTimer != null)
      clearTimeout(this.filterDebounceTimer);
    this.filterDebounceTimer = setTimeout(() => {
      this.filterDebounceTimer = null;
      this.onFilterChange();
    }, this.FILTER_DEBOUNCE_MS);
  }

  clearFilters(): void {
    this.filters = {
      action: '',
      entity: '',
      propertyId: this.isAdminRoute
        ? ''
        : this.localStorageService.getSessionPropertyStaff()?.tenantId ?? '',
      actorUserId: '',
      dateFrom: '',
      dateTo: '',
      search: '',
      page: 1,
      limit: this.limit,
    };
    this.dateFrom = null;
    this.dateTo = null;
    this.page = 1;
    this.router.navigate([], {
      queryParams: toQueryParams(this.getQueryParams()),
      queryParamsHandling: '',
    });
  }

  onDateFromSelect(date: Date | null): void {
    this.dateFrom = date;
  }

  onDateToSelect(date: Date | null): void {
    this.dateTo = date;
  }

  getActorDisplay(actor: AuditLogItem['actor']): string {
    if (!actor) return '—';
    const first = actor.firstName ?? '';
    const last = actor.lastName ?? '';
    if (first || last) return `${first} ${last}`.trim();
    return actor.email ?? actor.userId ?? '—';
  }

  /** Fallback when actor avatar image fails to load: hide img, show initial circle. */
  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) img.style.display = 'none';
    const next = img?.nextElementSibling as HTMLElement;
    if (next) next.style.display = 'inline-flex';
  }

  goToDetails(entity: string, entityId: string): void {
    const pathSegments =
      PMSRouteEnum.PMS_AUDIT_LOG_DETAILS.split('/').filter(Boolean);
    this.router.navigate([PMSRouteEnum.PMS_BASE_URL, ...pathSegments], {
      queryParams: { entity, entityId },
    });
  }

  private getQueryParams(): Record<string, unknown> {
    const qp: Record<string, unknown> = {
      page: this.page,
      limit: this.limit,
    };
    if (this.filters.action) qp['action'] = this.filters.action;
    if (this.filters.entity) qp['entity'] = this.filters.entity;
    if (this.filters.propertyId) qp['propertyId'] = this.filters.propertyId;
    if (this.filters.actorUserId) qp['actorUserId'] = this.filters.actorUserId;
    if (this.filters.search) qp['search'] = this.filters.search;
    if (this.dateFrom) qp['dateFrom'] = this.dateFrom;
    if (this.dateTo) qp['dateTo'] = this.dateTo;
    return qp;
  }
}
